<template>
  <section class="ma-4 ma-md-8">
    <t-page-title
      title="Cupons"
      :on-add="() => $router.push({ name: 'create-coupons' })"
    />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          ref="table"
          :headers="headers"
          :provider="fetch"
          :resource="resource"
          :actions="actions"
          :filters="filters"
        >
          <template #[`item.value`]="{ item }">
            {{
              item.method === "absolute"
                ? $formatters.currency(item.value / 100)
                : item.value + "%"
            }}
          </template>
          <template #[`item.method`]="{ item }">
            {{ item.method === "absolute" ? "Absoluto" : "Porcentagem" }}
          </template>
          <template #[`item.cumulative`]="{ item }">
            {{ item.cumulative ? "Sim" : "Não" }}
          </template>
          <template #[`item.expires_at`]="{ item }">
            {{
              $luxon.DateTime.fromSQL(item.expires_at).toFormat(
                "dd/MM/yyyy HH:mm:ss"
              )
            }}
          </template>
          <template #[`item.event_id`]="{ item }">
            {{
              eventFilter.items?.value.find((i) => i.value === item.event_id)?.title
            }}
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>
import { computed, ref, type Ref, useTemplateRef } from 'vue'
import DataTable, { type DataTableType } from '@/components/datatable/DataTable.vue'
import TPageTitle from '@/components/TPageTitle.vue'
import type { DataTableFilter, DataTableHeader, DefaultDataTableActions } from '@/types/DataTable'
import type Coupon from '@/types/Coupon'
import CouponService from '@/services/CouponService'
import ProducerService from '@/services/ProducerService'
import { useTextFilter } from '@/helpers/reports/textFilter'
import { useSelectFilter } from '@/helpers/reports/selectFilter'
import { useRouter } from 'vue-router'
import { useAbility } from '@casl/vue'
import { useSnackBar } from '@/stores/snackbar'
import { useLoader } from '@/stores/loader'
import { useDialog } from '@/stores/dialog'
import { DateTime } from 'luxon'

const { can } = useAbility()
const router = useRouter()
const snackbar = useSnackBar()
const loader = useLoader()
const dialog = useDialog()

const table = useTemplateRef<DataTableType>('table')

const resource = 'coupons'

const headers: DataTableHeader[] = [
  { title: 'ID', value: 'id', key: 'id', sortable: false },
  {
    title: 'Evento',
    value: 'event_id',
    key: 'event_id',
    sortable: false,
  },
  { title: 'Código Cupom', value: 'code', key: 'code', sortable: false },
  { title: 'Método', value: 'method', key: 'method', sortable: false },
  { title: 'Valor/%', value: 'value', key: 'value', sortable: false },
  {
    title: 'Itens por Resgate',
    value: 'max_items',
    key: 'max_items',
    sortable: false,
  },
  {
    title: 'Carga Limite',
    value: 'max_uses',
    key: 'max_uses',
    sortable: false,
  },
  {
    title: 'Resgates',
    value: 'uses_count',
    key: 'uses_count',
    sortable: false,
  },
  {
    title: 'Validade',
    value: 'expires_at',
    key: 'expires_at',
    sortable: false,
  },
  {
    title: 'Cumulativo',
    value: 'cumulative',
    key: 'cumulative',
    sortable: false,
  },
  {
    title: 'Ações',
    value: 'actions',
    key: 'actions',
    sortable: false,
    align: 'center',
  },
]

// The event filter starts with only a "Todos" (all) option, and has its real
// options appended once loadEventOptions() resolves, mirroring the original
// filterProvider's behavior.
const eventItems: Ref<{ title: string, value: string }[]> = ref([ { title: 'Todos', value: '' } ])
const eventModel: Ref<string | null> = ref('')
const eventReady = Promise.withResolvers<boolean>()

const eventFilter: DataTableFilter<string> = {
  model: eventModel,
  value: computed(() => eventModel.value),
  items: computed(() => eventItems.value),
  label: 'Evento',
  field: 'event_id',
  type: 'select',
  loading: ref(false),
  ready: eventReady.promise,
  required: false,
}

const filters: Ref<DataTableFilter[]> = ref([
  useTextFilter({ label: 'Código', field: 'code', required: false }),
  eventFilter,
  useSelectFilter({
    label: 'Método',
    field: 'method',
    icon: '',
    items: [
      { title: 'Todos', value: '' },
      { title: 'Absoluto', value: 'absolute' },
      { title: 'Porcentagem', value: 'percentage' },
    ],
    required: false,
    initial: '',
  }),
])

async function loadEventOptions() {
  const events = await new ProducerService().events(1, '', 50)

  eventItems.value = eventItems.value.concat(
    events.data.data.map(e => ({ title: e.title, value: e.id })),
  )

  eventReady.resolve(true)
}

loadEventOptions()

const actions: DefaultDataTableActions<Coupon> = {
  read: {
    callback: async item => router.push({ name: 'read-coupon', params: { id: item.id } }),
    condition: () => can('read', 'coupons'),
    icon: 'fa-solid fa-magnifying-glass',
    color: 'primary',
  },
  update: {
    callback: async item => router.push({ name: 'edit-coupon', params: { id: item.id } }),
    condition: item => can('update', 'coupons')
      && (!item.event || DateTime.fromSQL(item.event.dates.end) > DateTime.now()),
    icon: 'fa-solid fa-pen',
    color: 'info',
  },
  destroy: {
    callback: async item => destroy(item),
    condition: item => can('delete', 'coupons')
      && (!item.event || DateTime.fromSQL(item.event.dates.end) > DateTime.now()),
    icon: 'fa-solid fa-trash',
    color: 'error',
  },
}

async function fetch(page: number, filtersString: string, per_page?: number) {
  try {
    return await new CouponService().index(page, filtersString, per_page)
  } catch (error) {
    snackbar.show('Não foi possível buscar os cupons', 'error')
    throw error
  }
}

async function destroy(model: Coupon) {
  const confirm = await dialog.open({
    title: 'Confirmação de exclusão',
    message: 'Realmente deseja excluir o cupom? Essa ação é irreversível',
  })

  if (!confirm) return

  loader.increment()
  try {
    await new CouponService().destroy(model)

    table.value?.search()

    snackbar.show('Cupom excluído com sucesso', 'success')
  } catch (error) {
    snackbar.show('Não foi possível buscar os cupons', 'error')
    throw error
  } finally {
    loader.decrement()
  }
}
</script>
